import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useContext } from 'react';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { USER } from 'self-service-api/constants/UnitTypes';
import { skuSelector } from '../../selectors/ProductsSelectors';
import classNames from '../../utils/classNames';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H5 from 'UIComponents/elements/headings/H5';
import { MONTHLY } from 'self-service-api/constants/Terms';
import { PlanBuilderContext } from '../../context/PlanBuilderContext';
import AdditionalUnitsPicker from './AdditionalUnitsPicker';
import { useAppSelector } from '../../reducers';
import SeatsPicker from '../common/quantityPacks/SeatsPicker';
import { Divider } from './SharedComponents';
import { usePricingLibSelector } from '../../selectors/SelectorUtils';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';

const QuantityPacksPickerContainer = ({
  additionalUnitSkuIds,
  setProductLevel,
  trackingSource,
  showEndDivider,
  productLevel
}) => {
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const {
    productConfigurations,
    setProductConfigurations,
    showDiscountedPricesInUnitsPicker,
    queryParamTerm
  } = useContext(PlanBuilderContext);

  const getSelectedAdditionalUnitsQuantity = additionalUnitSkuId => {
    const foundConfig = productConfigurations.find(({
      skuId
    }) => skuId === additionalUnitSkuId);
    return foundConfig ? foundConfig.quantity : 0;
  };

  const setAdditionalUnitQuantity = (quantity, additionalUnitSkuId) => {
    setProductConfigurations(() => {
      const filteredConfigs = productConfigurations.filter(({
        skuId
      }) => skuId !== additionalUnitSkuId);

      if (quantity > 0) {
        filteredConfigs.push(createProductConfig({
          skuId: additionalUnitSkuId,
          quantity
        }));
      }

      return filteredConfigs;
    });
  };

  const quantityPackSkuIds = useAppSelector(state => {
    return additionalUnitSkuIds.filter(skuId => skuSelector(state, {
      skuId
    }).quantityUnit);
  });
  const nonUserQuantityPackSkuIds = useAppSelector(state => {
    return quantityPackSkuIds.filter(skuId => skuSelector(state, {
      skuId
    }).quantityUnit.unitType !== USER);
  });
  const usersAdditionalUnitsSkuIds = useAppSelector(state => {
    return quantityPackSkuIds.filter(skuId => skuSelector(state, {
      skuId
    }).quantityUnit.unitType === USER);
  }); // Need to group the marketing contacts in a way that looks nice

  const nonUserPickerComponents = nonUserQuantityPackSkuIds.map(quantityPackSkuId => /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Divider, {
      className: "m-y-7"
    }), /*#__PURE__*/_jsx(AdditionalUnitsPicker, {
      additionalUnitSkuId: quantityPackSkuId,
      additionalUnitQuantity: getSelectedAdditionalUnitsQuantity(quantityPackSkuId),
      setAdditionalUnitQuantity: (quantity, skuId = quantityPackSkuId) => setAdditionalUnitQuantity(quantity, skuId),
      productConfigurations: productConfigurations,
      setActiveProductLevel: setProductLevel,
      queryParamTerm: showDiscountedPricesInUnitsPicker ? queryParamTerm : MONTHLY,
      trackingSource: trackingSource,
      setProductConfigurations: setProductConfigurations,
      productLevel: productLevel,
      skuId: quantityPackSkuId
    })]
  }, quantityPackSkuId));
  const hasMultipleUserPickers = usersAdditionalUnitsSkuIds.length > 1;
  const shouldShowDivider = isSeatsRedesignUngated && hasMultipleUserPickers || !isSeatsRedesignUngated && !hasMultipleUserPickers || isSeatsRedesignUngated && nonUserQuantityPackSkuIds.length === 0; // Need to group the sales / service seats in a way that looks nice

  const userPickerComponents = usersAdditionalUnitsSkuIds.map((additionalUnitSkuId, index) => /*#__PURE__*/_jsxs(Fragment, {
    children: [shouldShowDivider && /*#__PURE__*/_jsx(Divider, {
      className: isSeatsRedesignUngated ? 'm-y-10' : 'm-y-7'
    }), /*#__PURE__*/_jsx(SeatsPicker, {
      additionalUnitSkuId: additionalUnitSkuId,
      additionalUnitQuantity: getSelectedAdditionalUnitsQuantity(additionalUnitSkuId),
      onChange: quantity => setAdditionalUnitQuantity(quantity, additionalUnitSkuId),
      productConfigurations: productConfigurations,
      useProductLineSpecificTitle: usersAdditionalUnitsSkuIds.length > 1,
      hideFreeUsersDisclaimer: index < usersAdditionalUnitsSkuIds.length - 1,
      className: classNames(hasMultipleUserPickers && 'm-top-6'),
      queryParamTerm: showDiscountedPricesInUnitsPicker ? queryParamTerm : MONTHLY,
      trackingSource: trackingSource
    })]
  }, additionalUnitSkuId));
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isSeatsRedesignUngated ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [userPickerComponents, nonUserPickerComponents]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [nonUserPickerComponents, hasMultipleUserPickers ? /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(Divider, {
          className: isSeatsRedesignUngated ? 'm-y-10' : 'm-y-7'
        }), /*#__PURE__*/_jsx(H5, {
          "aria-level": 3,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.paidUsers"
          })
        })]
      }, "seatsTitle") : null, userPickerComponents]
    }), showEndDivider && /*#__PURE__*/_jsx(Divider, {
      className: isSeatsRedesignUngated ? 'm-y-10' : 'm-y-7'
    })]
  });
};

export default QuantityPacksPickerContainer;